import React, { useState } from 'react';
import {
	TimelineCalendarEvent,
	TimelineCalendarOnDateClick,
} from '../../Calendar/Components/TimelineCalendar/TimelineCalendar';
import { Moment } from 'moment';
import { CalendarView } from '../../Calendar/Enums/CalendarView.eunm';
import { ContextMenuParams } from '../../../Components/ContextMenu/ContextMenu';
import { ServiceSchedule, ServiceScheduleItem } from '../../../graphql/graphql';
import moment from 'moment';
import { EventClickArg, EventContentArg } from '@fullcalendar/react';
import apiClient from '../../../Services/ApiClient';
import Tooltip from '../../../Components/Popup/Tooltip';
import { formatObjectForQuery } from '../../../Services/Api/FormatObjectForQuery';
import useApiClient from '../../Base/Hooks/useApiClient';
import NoEscape from '../../../Components/NoEscape';

export default function useCalendarWithSchedules(workplaceId: string) {
	const [events, setEvents] = useState<TimelineCalendarEvent[]>([]);
	const [dateRange, setDateRange] = useState<
		{ start: Moment; end: Moment } | undefined
	>(undefined);
	const [currentCalendarView, setCurrentCalendarView] = useState<
		CalendarView | undefined
	>(undefined);
	const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
	const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
	const [contextMenuProps, setContextMenuProps] = useState<ContextMenuParams>({
		isOpen: false,
	});
	const [selectedDate, setSelectedDate] = useState<Moment | undefined>(
		moment(),
	);
	const [selectedResourceId, setSelectedResourceId] = useState<
		string | undefined
	>(undefined);
	const [selectedResourceTitle, setSelectedResourceTitle] = useState<
		string | undefined
	>();
	const [selectedServiceSchedule, setSelectedServiceSchedule] = useState<
		ServiceSchedule | undefined
	>(undefined);
	const [selectedServiceScheduleItem, setSelectedServiceScheduleItem] =
		useState<ServiceScheduleItem | undefined>(undefined);
	const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
		useState(false);
	const [
		isMultipleDeleteConfirmationOpen,
		setIsMultipleDeleteConfirmationOpen,
	] = useState(false);
	const [isInstructionModalOpen, setIsInstructionModalOpen] = useState(true);
	const [isLoading, setIsLoading] = useState(false);
	const { displayError, showUnexpectedError } = useApiClient();

	//FC TO STORE IN STATE CURRENT CALENDAR VIEWED DATE RANGE
	const handleDateRangeChange = (start: Moment, end: Moment) => {
		setDateRange({ start, end });
	};

	//FC TO STORE IN STATE CURRENT CALENDAR VIEW
	const handleChangeView = (view: CalendarView) => {
		setCurrentCalendarView(view);
	};

	//FC TO HANDLE CLICK ON DATE AND STORE DATE AND RESOURCE INTO STATES THEN OPEN CREATE SCHEDULE MODAL
	const handleDateClick: TimelineCalendarOnDateClick = (
		start: Moment,
		end: Moment,
		resourceId: string,
		resourceTitle?: string,
	) => {
		setSelectedDate(start);
		setSelectedResourceId(resourceId);
		setSelectedResourceTitle(resourceTitle);
		setContextMenuProps({
			isOpen: false,
		});
		setIsCreateModalOpen(true);
	};

	// FC TO HANDLE CLICK ON EVENT AND STORE SCHEDULE DATA TO STATES AND OPEN CONTEXT MENU
	const handleEventClick = (data: EventClickArg) => {
		setSelectedDate(data.event.extendedProps.timeFrom);
		setSelectedServiceSchedule(data.event.extendedProps.serviceSchedule);
		setSelectedServiceScheduleItem(
			data.event.extendedProps.serviceScheduleItem,
		);
		setSelectedResourceId(data.event.extendedProps.resourceId);
		setSelectedResourceTitle(data.event.extendedProps.resourceTitle);
		if ('clientX' in data.jsEvent && 'clientY' in data.jsEvent) {
			setContextMenuProps({
				isOpen: true,
				clientX: data.jsEvent.clientX as number,
				clientY: data.jsEvent.clientY as number,
			});
		}
	};

	//FC TO WIPE MULTIPLE STATES AT SAME TIME
	const handleWipeStates = () => {
		setSelectedDate(undefined);
		setSelectedServiceSchedule(undefined);
		setSelectedServiceScheduleItem(undefined);
		setSelectedResourceTitle(undefined);
		setSelectedResourceId(undefined);
		setIsCreateModalOpen(false);
		setIsEditModalOpen(false);
	};

	//FC TO CLOSE CREATE NEW SCHEDULE MODAL
	const handleCloseCreateModal = () => {
		setIsCreateModalOpen(false);
	};

	//FC TO CLOSE EDIT SCHEDULE MODAL
	const handleCloseUpdateModal = () => {
		setIsEditModalOpen(false);
	};

	//FC TO OPEN MODAL WITH CONFIRMATION OF SINGLE SCHEDULE DELETE
	const handleOpenRemoveSingle = () => {
		setContextMenuProps({ isOpen: false });
		setIsDeleteConfirmationOpen(true);
	};

	//FC TO OPEN MODAL WITH CONFIRMATION OF REPEATABLE SCHEDULE DELETE
	const handleOpenRemoveMultiple = () => {
		setContextMenuProps({ isOpen: false });
		setIsMultipleDeleteConfirmationOpen(true);
	};

	//FC TO OPEN MODAL WITH OPTIONS WHAT USER WHANTS TO DELETE
	const handleDeleteServiceSchedule = () => {
		handleCloseUpdateModal();
		if (selectedServiceScheduleItem?.repeatable) {
			handleOpenRemoveMultiple();
		} else {
			handleOpenRemoveSingle();
		}
	};

	//FC TO PROCESS DELETE TO SERVICE SCHEDULE
	const handleRemoveEvent = async (
		excludeFuture: boolean,
		onRemove: () => void,
	) => {
		setIsLoading(true);
		setContextMenuProps({ isOpen: false });
		if (selectedServiceSchedule && selectedServiceScheduleItem) {
			try {
				const params = formatObjectForQuery({
					excludeDay: moment(
						`${moment(selectedDate).format('YYYY-MM-DD')}T${
							selectedServiceScheduleItem.timeFrom
						}`,
					).toISOString(),
					excludeFuture,
				});

				const response = await apiClient.delete(
					`/workplaces/${workplaceId}/services-schedule/${selectedServiceSchedule.id}/items/${selectedServiceScheduleItem.id}?${params}`,
				);

				if (response.status === 200) {
					onRemove();
				} else {
					showUnexpectedError();
				}
			} catch (e) {
				displayError(e);
			}
		}
		setIsLoading(false);
	};

	//FC TO DELETE SERVICE SCHEDULE AND WIPE STATES AND CLOSE MODALS
	const handleRemove = async (multiple: boolean, onRemove: () => void) => {
		await handleRemoveEvent(multiple, onRemove);
		handleWipeStates();
		setIsDeleteConfirmationOpen(false);
		setIsMultipleDeleteConfirmationOpen(false);
	};

	//FC TO HANDLE EVENT RENDER DEPENDING ON CURRENT CALENDAR VIEW
	const handleRenderEvent = (arg: EventContentArg) => {
		const event = arg.event;

		if (
			currentCalendarView === CalendarView.LIST_WEEK ||
			currentCalendarView === CalendarView.TIMELINE_DAY
		) {
			return event.title;
		}

		return (
			<Tooltip trigger={event.title}>
				<NoEscape>{event.extendedProps.tooltipText}</NoEscape>
			</Tooltip>
		);
	};

	return {
		events,
		setEvents,
		dateRange,
		setDateRange,
		currentCalendarView,
		setCurrentCalendarView,
		isCreateModalOpen,
		setIsCreateModalOpen,
		isEditModalOpen,
		setIsEditModalOpen,
		contextMenuProps,
		setContextMenuProps,
		selectedDate,
		setSelectedDate,
		selectedResourceId,
		setSelectedResourceId,
		selectedResourceTitle,
		setSelectedResourceTitle,
		selectedServiceSchedule,
		setSelectedServiceSchedule,
		selectedServiceScheduleItem,
		setSelectedServiceScheduleItem,
		isDeleteConfirmationOpen,
		setIsDeleteConfirmationOpen,
		isMultipleDeleteConfirmationOpen,
		setIsMultipleDeleteConfirmationOpen,
		isInstructionModalOpen,
		setIsInstructionModalOpen,
		isLoading,
		setIsLoading,
		handleDateRangeChange,
		handleChangeView,
		handleDateClick,
		handleEventClick,
		handleWipeStates,
		handleCloseCreateModal,
		handleCloseUpdateModal,
		handleOpenRemoveSingle,
		handleOpenRemoveMultiple,
		handleDeleteServiceSchedule,
		handleRemove,
		handleRenderEvent,
	};
}
